<script setup lang="ts">
import { useJournalSettings } from '../lib/useJournalData';

const { settings } = await useJournalSettings();

// tel: takes the digits only, the layout keeps the human spacing
const phoneHref = computed(
  () => `tel:${settings.value.advertising_phone.replace(/[^\d+]/g, '')}`,
);
</script>

<template>
  <section :class="$style.root">
    <h2 :class="$style.heading">
      По вопросам размещения рекламы в печатных изданиях обращайтесь:
    </h2>
    <div :class="$style.card">
      <div :class="$style.field">
        <span :class="$style.label">Электронная почта</span>
        <a
          :href="`mailto:${settings.advertising_email}`"
          :class="[$style.value, $style.value_accent]"
        >
          {{ settings.advertising_email }}
        </a>
      </div>
      <div :class="$style.field">
        <span :class="$style.label">Телефон</span>
        <a :href="phoneHref" :class="$style.value">
          {{ settings.advertising_phone }}
        </a>
      </div>
    </div>
  </section>
</template>

<style module lang="scss">
.root {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-5x) var(--space-2-5x);
  background-color: var(--color-black-1);
}

// Both the heading and the card fill the 556px content width of the block
.heading {
  @include text_h4;
  color: var(--color-white);
  width: 100%;
  max-width: 556px;
  margin: 0 0 var(--space-3x);
  text-align: center;
}

.card {
  display: flex;
  width: 100%;
  max-width: 556px;
  background-color: var(--color-white);
}

// Two equal halves, 78px tall together with the 20px padding of the layout
.field {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-0-5x);
  padding: var(--space-2-5x);

  & + & {
    border-left: 1px solid var(--color-gray-line);
  }
}

.label {
  @include text_caption2_caps;
  color: var(--color-gray-text);
}

// Only the address is red in the layout, the phone stays black
.value {
  @include text_caption1;
  color: var(--color-black-1);
  text-decoration: none;
  text-transform: uppercase;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }

  &_accent {
    color: var(--color-red-1);
  }
}

@media (max-width: 599px) {
  /*
   * The same 20px of black on every side of the card, which is what makes it
   * 320 wide on a 360 screen — the width the layout draws
   */
  .root {
    padding: var(--space-2-5x);
  }

  // "Body 0 Medium" on mobile: 18/500, 120%
  .heading {
    @include text_body1_large_medium;
    margin-bottom: var(--space-2x);
  }

  .card {
    flex-direction: column;
  }

  // Tighter than the desktop cell vertically, wider on the sides
  .field {
    padding: var(--space-1-5x) var(--space-3x);
  }

  .field + .field {
    border-left: none;
    border-top: 1px solid var(--color-gray-line);
  }

  // Sentence case rather than the caps of the wide layout
  .label {
    @include text_caption1;
    text-transform: none;
    letter-spacing: normal;
  }

  // 16/400 and the address as written, not upper-cased
  .value {
    @include text_body1;
    text-transform: none;
  }
}
</style>
